import React from "react";
import { BanknotesIcon } from "@heroicons/react/24/outline";

const SubscriptionBillingTab = ({
  isOrgOwner,
  organization,
  user,
  subscriptionDetails,
  loadingDetails,
  paymentCountry,
  viewPortal,
  cancellationDate,
  isCancellationScheduled,
  onCancelSubscription,
  onReactivateSubscription,
  isCancelling,
  isReactivating,
  onShowCancelConfirm,
  isINR,
}) => {
  if (!isOrgOwner) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-base font-semibold text-gray-900">Billing</h2>
          <p className="text-xs text-gray-500 mt-0.5">
            Payment methods and transaction history
          </p>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
          <div className="text-center py-16 p-6">
            <BanknotesIcon className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-600 font-medium">Billing & Payment</p>
            <p className="text-sm text-gray-500 mt-1">
              Only organization admins can manage billing and payment methods.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const hasSubscription =
    organization?.isSubscribed &&
    (organization?.stripeSubscriptionId ||
      organization?.razorpaySubscriptionId);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-base font-semibold text-gray-900">Billing</h2>
        <p className="text-xs text-gray-500 mt-0.5">
          Payment methods and transaction history
        </p>
      </div>
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
        <div className="p-6 sm:p-8 space-y-8">
          {hasSubscription && (
            <div className="space-y-8">
              {/* Payment Method */}
              <div className="p-4 rounded-xl bg-gray-50/80 border border-gray-100">
                <h3 className="text-sm font-semibold text-gray-900 mb-4">
                  Payment Method
                </h3>
                {loadingDetails ? (
                  <div className="flex items-center gap-2 text-gray-500">
                    <svg
                      className="animate-spin h-5 w-5"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                      />
                    </svg>
                    Loading…
                  </div>
                ) : subscriptionDetails?.paymentMethod ? (
                  subscriptionDetails.paymentMethod.type === "card" ? (
                    <div className="relative w-full max-w-sm overflow-hidden rounded-2xl shadow-lg ring-1 ring-black/5">
                      <div className="aspect-[1.586/1] bg-gradient-to-br from-slate-600 via-slate-700 to-slate-800 p-6 flex flex-col justify-between">
                        <div className="flex items-start justify-between">
                          <div className="w-10 h-8 bg-amber-400/90 rounded-sm flex items-center justify-center">
                            <div className="w-6 h-4 border border-amber-600/50 rounded-[2px] bg-gradient-to-br from-amber-200/20 to-amber-400/30" />
                          </div>
                          <span className="text-white/90 text-xs font-medium tracking-widest uppercase">
                            {subscriptionDetails.paymentMethod.brand || "Card"}
                          </span>
                        </div>
                        <div>
                          <p className="text-white/95 font-mono text-lg tracking-[0.25em] mb-3">
                            •••• •••• ••••{" "}
                            {subscriptionDetails.paymentMethod.last4}
                          </p>
                          {subscriptionDetails.paymentMethod.name && (
                            <p className="text-white/90 text-sm font-medium uppercase tracking-wider mb-2">
                              {subscriptionDetails.paymentMethod.name}
                            </p>
                          )}
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-white/50 text-[10px] uppercase tracking-wider mb-0.5">
                                Valid Thru
                              </p>
                              <p className="text-white/90 font-mono text-sm">
                                {subscriptionDetails.paymentMethod.expMonth &&
                                subscriptionDetails.paymentMethod.expYear
                                  ? `${String(
                                      subscriptionDetails.paymentMethod.expMonth
                                    ).padStart(2, "0")}/${String(
                                      subscriptionDetails.paymentMethod.expYear
                                    ).slice(-2)}`
                                  : "••/••"}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : subscriptionDetails.paymentMethod.type === "upi" ? (
                    <div className="relative w-full max-w-sm overflow-hidden rounded-2xl shadow-md border border-indigo-100 bg-white">
                      <div className="bg-gradient-to-br from-indigo-50 via-white to-purple-50 p-6">
                        <div className="flex items-center gap-4">
                          <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-md">
                            <svg
                              className="w-8 h-8 text-white"
                              viewBox="0 0 24 24"
                              fill="currentColor"
                            >
                              <path d="M17 1.01L7 1c-1.1 0-2 .9-2 2v18c0 1.1.9 2 2 2h10c1.1 0 2-.9 2-2V3c0-1.1-.9-1.99-2-1.99zM17 19H7V5h10v14z" />
                            </svg>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-medium text-indigo-600 uppercase tracking-wider mb-0.5">
                              UPI
                            </p>
                            <p className="text-gray-900 font-semibold truncate">
                              {subscriptionDetails.paymentMethod.vpa ||
                                "Linked UPI ID"}
                            </p>
                            <p className="text-xs text-gray-500 mt-0.5">
                              {subscriptionDetails.paymentMethod.name ||
                              user?.name
                                ? `Linked to ${subscriptionDetails.paymentMethod.name || user?.name}`
                                : "Unified Payments Interface"}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl border border-gray-100">
                      <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                        <svg
                          className="w-6 h-6 text-primary"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z"
                          />
                        </svg>
                      </div>
                      <div>
                        <p className="font-medium text-gray-900 capitalize">
                          {subscriptionDetails.paymentMethod.type}
                        </p>
                        {subscriptionDetails.paymentMethod.vpa && (
                          <p className="text-sm text-gray-600">
                            {subscriptionDetails.paymentMethod.vpa}
                          </p>
                        )}
                      </div>
                    </div>
                  )
                ) : (
                  <p className="text-gray-500">
                    No payment method on file.{" "}
                    {paymentCountry === "US" &&
                      "Use the billing portal below to add one."}
                  </p>
                )}
              </div>

              {/* Transaction History */}
              <div className="p-4 rounded-xl bg-gray-50/80 border border-gray-100">
                <h3 className="text-sm font-semibold text-gray-900 mb-4">
                  Transaction History
                </h3>
                {loadingDetails ? (
                  <div className="text-gray-500 py-8 text-center">
                    Loading transactions…
                  </div>
                ) : subscriptionDetails?.invoices?.length > 0 ? (
                  <div className="overflow-x-auto rounded-xl border border-gray-200">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Invoice
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Date
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Amount
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Status
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Invoice
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-100">
                        {subscriptionDetails.invoices.map((inv) => (
                          <tr key={inv.id} className="hover:bg-gray-50">
                            <td className="px-4 py-3 text-sm font-medium text-gray-900">
                              {inv.number || inv.id}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600">
                              {inv.date
                                ? new Date(
                                    typeof inv.date === "number"
                                      ? inv.date * 1000
                                      : inv.date
                                  ).toLocaleDateString("en-US", {
                                    year: "numeric",
                                    month: "short",
                                    day: "numeric",
                                  })
                                : "—"}
                            </td>
                            <td className="px-4 py-3 text-sm font-medium text-gray-900">
                              {inv.currency === "INR" ? "₹" : "$"}
                              {(inv.amount || 0).toFixed(2)}
                            </td>
                            <td className="px-4 py-3">
                              <span
                                className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                                  inv.status === "paid"
                                    ? "bg-green-100 text-green-700"
                                    : inv.status === "open"
                                      ? "bg-blue-100 text-blue-700"
                                      : "bg-gray-100 text-gray-700"
                                }`}
                              >
                                {inv.status}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              {(inv.invoicePdf ||
                                inv.hostedInvoiceUrl ||
                                inv.shortUrl) && (
                                <a
                                  href={
                                    inv.invoicePdf ||
                                    inv.hostedInvoiceUrl ||
                                    inv.shortUrl
                                  }
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-primary hover:text-primary/80 text-sm font-medium"
                                >
                                  {inv.invoicePdf
                                    ? "Download PDF"
                                    : "View invoice"}
                                </a>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-gray-500 py-6">No transactions yet.</p>
                )}
              </div>
            </div>
          )}

          {/* Subscription Management — US */}
          {paymentCountry === "US" && (
            <div className="p-4 rounded-xl bg-gray-50/80 border border-gray-100">
              <h3 className="text-sm font-semibold text-gray-900 mb-4">
                Subscription Management
              </h3>
              <div>
                <div className="flex items-center justify-between mb-8">
                  <div className="flex items-center space-x-4">
                    <div className="w-14 h-14 bg-primary rounded-xl flex items-center justify-center">
                      <svg
                        className="w-8 h-8 text-white"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
                        />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-gray-900">
                        Billing & Subscription
                      </h3>
                      <p className="text-gray-600">
                        Secure payment management with industry-leading security
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse" />
                    <span className="text-sm text-green-600 font-bold px-3 py-1 rounded-full">
                      Active
                    </span>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6 mb-8">
                  <div className="p-4 bg-white border border-gray-100 rounded-xl">
                    <div className="flex items-center space-x-4 mb-4">
                      <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center">
                        <svg
                          className="w-6 h-6 text-primary"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                      </div>
                      <h4 className="text-lg font-bold text-gray-900">
                        Update Billing
                      </h4>
                    </div>
                    <p className="text-gray-600">
                      Change payment methods and update billing information
                      securely
                    </p>
                  </div>

                  <div className="p-4 bg-white border border-gray-100 rounded-xl">
                    <div className="flex items-center space-x-4 mb-4">
                      <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center">
                        <svg
                          className="w-6 h-6 text-primary"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                          />
                        </svg>
                      </div>
                      <h4 className="text-lg font-bold text-gray-900">
                        View History
                      </h4>
                    </div>
                    <p className="text-gray-600">
                      Access your complete billing history and download invoices
                    </p>
                  </div>
                </div>

                <div className="p-6 bg-white border border-gray-200 rounded-xl">
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h4 className="text-xl font-bold text-gray-900 mb-2">
                        Secure Stripe Portal
                      </h4>
                      <p className="text-gray-600">
                        Manage your subscription through our trusted payment
                        partner with enterprise-grade security
                      </p>
                    </div>
                    <div className="flex items-center space-x-3">
                      <svg
                        className="w-6 h-6 text-green-500"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <span className="text-sm text-green-600 font-bold px-3 py-1 rounded-full">
                        256-bit SSL
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-6">
                      <div className="flex items-center space-x-2">
                        <div className="w-3 h-3 bg-primary rounded-full" />
                        <span className="text-sm text-gray-600 font-medium">
                          PCI DSS Compliant
                        </span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <div className="w-3 h-3 bg-green-500 rounded-full" />
                        <span className="text-sm text-gray-600 font-medium">
                          SOC 2 Certified
                        </span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <div className="w-3 h-3 bg-primary rounded-full" />
                        <span className="text-sm text-gray-600 font-medium">
                          GDPR Compliant
                        </span>
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        viewPortal?.();
                      }}
                      className="bg-primary text-white px-8 py-4 rounded-xl text-sm font-bold hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 transition-all duration-300 transform hover:scale-105"
                    >
                      <div className="flex items-center space-x-3">
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                          />
                        </svg>
                        <span>Open Billing Portal</span>
                      </div>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Subscription Management — INR (Razorpay) */}
          {isINR && organization?.isSubscribed && (
            <div className="p-4 rounded-xl bg-gray-50/80 border border-gray-100">
              <h3 className="text-sm font-semibold text-gray-900 mb-4">
                Subscription Management
              </h3>
              <div>
                {isCancellationScheduled ? (
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-3 h-3 bg-orange-500 rounded-full" />
                        <h3 className="text-lg font-bold text-gray-900">
                          Cancellation Scheduled
                        </h3>
                      </div>
                      <p className="text-gray-600 text-sm">
                        Your subscription is set to cancel
                        {cancellationDate ? (
                          <span className="font-medium text-gray-800">
                            {" "}
                            on {cancellationDate}
                          </span>
                        ) : (
                          " at the end of your current billing period"
                        )}
                        . You can still use all features until then.
                      </p>
                      <p className="text-xs text-gray-400 mt-1">
                        Note: Razorpay subscriptions cannot be un-cancelled.
                        You will need to resubscribe after the billing period
                        ends.
                      </p>
                    </div>
                    <button
                      onClick={onReactivateSubscription}
                      disabled={isReactivating}
                      className="flex-shrink-0 bg-primary text-white px-6 py-3 rounded-xl text-sm font-bold hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 transition-all disabled:opacity-60"
                    >
                      {isReactivating
                        ? "Processing..."
                        : "Keep Subscription"}
                    </button>
                  </div>
                ) : (
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse" />
                        <h3 className="text-lg font-bold text-gray-900">
                          Active Subscription
                        </h3>
                      </div>
                      <p className="text-gray-600 text-sm">
                        Your subscription renews automatically
                        {cancellationDate ? (
                          <span>
                            {" "}
                            on{" "}
                            <span className="font-medium text-gray-800">
                              {cancellationDate}
                            </span>
                          </span>
                        ) : null}
                        . You can cancel anytime and continue using your plan
                        until the billing period ends.
                      </p>
                    </div>
                    <button
                      onClick={onShowCancelConfirm}
                      className="flex-shrink-0 border border-red-300 text-red-600 px-6 py-3 rounded-xl text-sm font-bold hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-400 focus:ring-offset-2 transition-all"
                    >
                      Cancel Subscription
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SubscriptionBillingTab;
